import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import {
  MuiPickersUtilsProvider,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  datePicker: {
    width: '100%',
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const TripEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [formData, setFormData] = useState({
    routeId: '',
    departureDate: new Date(),
    arrivalDate: new Date(),
    vehicleId: '',
    driverId: '',
    price: '',
    status: 'Scheduled',
    notes: ''
  });
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (selectedRoute && formData.departureDate && !originalData) {
      // Calculer automatiquement la date d'arrivée en fonction de la durée de l'itinéraire
      const departureTime = new Date(formData.departureDate).getTime();
      const durationInMs = selectedRoute.duration * 60 * 1000; // Convertir minutes en millisecondes
      const arrivalTime = new Date(departureTime + durationInMs);
      
      setFormData({
        ...formData,
        arrivalDate: arrivalTime
      });
    }
  }, [selectedRoute, formData.departureDate, originalData]);

  useEffect(() => {
    if (originalData) {
      // Vérifier si des modifications ont été apportées
      const hasFormChanges = Object.keys(formData).some(key => {
        if (key === 'departureDate' || key === 'arrivalDate') {
          const originalDate = new Date(originalData[key]).getTime();
          const currentDate = new Date(formData[key]).getTime();
          return originalDate !== currentDate;
        }
        return originalData[key] !== formData[key];
      });
      
      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      // Récupérer les données du voyage
      const tripDoc = doc(firestore, 'trips', id);
      const tripSnapshot = await getDoc(tripDoc);
      
      let tripData;
      if (tripSnapshot.exists()) {
        tripData = {
          id: tripSnapshot.id,
          ...tripSnapshot.data()
        };
      } else {
        // Si le voyage n'existe pas, utiliser des données de démonstration
        tripData = {
          id: id,
          routeId: '1',
          routeName: 'Paris - Lyon',
          departureDate: '2025-04-15T08:00:00',
          arrivalDate: '2025-04-15T12:30:00',
          status: 'Scheduled',
          availableSeats: 45,
          bookedSeats: 32,
          price: 45.99,
          vehicleId: 'V001',
          vehicleName: 'Bus 101',
          driverId: 'D001',
          driverName: 'Jean Dupont',
          notes: 'Voyage régulier entre Paris et Lyon. Arrêt de 15 minutes à mi-parcours.',
          createdAt: new Date().toISOString()
        };
      }
      
      // Formater les dates
      const formattedData = {
        ...tripData,
        departureDate: new Date(tripData.departureDate),
        arrivalDate: new Date(tripData.arrivalDate),
        price: tripData.price.toString()
      };
      
      setFormData(formattedData);
      setOriginalData(formattedData);
      
      // Récupérer les itinéraires
      const routesCollection = collection(firestore, 'routes');
      const routesSnapshot = await getDocs(routesCollection);
      
      if (routesSnapshot.empty) {
        // Données de démonstration pour les itinéraires
        const demoRoutes = [
          { 
            id: '1', 
            name: 'Paris - Lyon', 
            origin: 'Paris',
            destination: 'Lyon',
            distance: 465,
            duration: 270, // en minutes
            status: 'Active',
            price: 45.99
          },
          { 
            id: '2', 
            name: 'Lyon - Marseille', 
            origin: 'Lyon',
            destination: 'Marseille',
            distance: 315,
            duration: 180, // en minutes
            status: 'Active',
            price: 35.50
          },
          { 
            id: '3', 
            name: 'Paris - Nice', 
            origin: 'Paris',
            destination: 'Nice',
            distance: 932,
            duration: 540, // en minutes
            status: 'Active',
            price: 89.99
          }
        ];
        setRoutes(demoRoutes);
        
        // Définir l'itinéraire sélectionné
        const selectedRoute = demoRoutes.find(route => route.id === formattedData.routeId);
        setSelectedRoute(selectedRoute);
      } else {
        const fetchedRoutes = routesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(route => route.status === 'Active'); // Ne montrer que les itinéraires actifs
        
        setRoutes(fetchedRoutes);
        
        // Définir l'itinéraire sélectionné
        const selectedRoute = fetchedRoutes.find(route => route.id === formattedData.routeId);
        setSelectedRoute(selectedRoute);
      }
      
      // Récupérer les véhicules
      const vehiclesCollection = collection(firestore, 'vehicles');
      const vehiclesSnapshot = await getDocs(vehiclesCollection);
      
      if (vehiclesSnapshot.empty) {
        // Données de démonstration pour les véhicules
        const demoVehicles = [
          { 
            id: 'V001', 
            name: 'Bus 101', 
            type: 'Bus',
            capacity: 45,
            licensePlate: 'AB-123-CD',
            status: 'Available'
          },
          { 
            id: 'V002', 
            name: 'Bus 102', 
            type: 'Bus',
            capacity: 45,
            licensePlate: 'EF-456-GH',
            status: 'Available'
          },
          { 
            id: 'V003', 
            name: 'Bus 103', 
            type: 'Bus',
            capacity: 45,
            licensePlate: 'IJ-789-KL',
            status: 'Available'
          }
        ];
        setVehicles(demoVehicles);
      } else {
        const fetchedVehicles = vehiclesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(vehicle => vehicle.status === 'Available' || vehicle.id === formattedData.vehicleId);
        
        setVehicles(fetchedVehicles);
      }
      
      // Récupérer les chauffeurs
      const driversCollection = collection(firestore, 'drivers');
      const driversSnapshot = await getDocs(driversCollection);
      
      if (driversSnapshot.empty) {
        // Données de démonstration pour les chauffeurs
        const demoDrivers = [
          { 
            id: 'D001', 
            name: 'Jean Dupont', 
            licenseNumber: 'DRV12345',
            status: 'Available'
          },
          { 
            id: 'D002', 
            name: 'Marie Martin', 
            licenseNumber: 'DRV67890',
            status: 'Available'
          },
          { 
            id: 'D003', 
            name: 'Pierre Durand', 
            licenseNumber: 'DRV54321',
            status: 'Available'
          }
        ];
        setDrivers(demoDrivers);
      } else {
        const fetchedDrivers = driversSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(driver => driver.status === 'Available' || driver.id === formattedData.driverId);
        
        setDrivers(fetchedDrivers);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données',
        severity: 'error'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'routeId') {
      const route = routes.find(r => r.id === value);
      setSelectedRoute(route);
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleDateChange = (name) => (date) => {
    setFormData({
      ...formData,
      [name]: date
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.routeId) {
      newErrors.routeId = 'L\'itinéraire est requis';
    }
    
    if (!formData.departureDate) {
      newErrors.departureDate = 'La date de départ est requise';
    } else if (new Date(formData.departureDate) < new Date() && formData.status === 'Scheduled') {
      newErrors.departureDate = 'La date de départ doit être dans le futur pour un voyage programmé';
    }
    
    if (!formData.arrivalDate) {
      newErrors.arrivalDate = 'La date d\'arrivée est requise';
    } else if (new Date(formData.arrivalDate) <= new Date(formData.departureDate)) {
      newErrors.arrivalDate = 'La date d\'arrivée doit être postérieure à la date de départ';
    }
    
    if (!formData.vehicleId) {
      newErrors.vehicleId = 'Le véhicule est requis';
    }
    
    if (!formData.driverId) {
      newErrors.driverId = 'Le chauffeur est requis';
    }
    
    if (!formData.price) {
      newErrors.price = 'Le prix est requis';
    } else if (isNaN(formData.price) || parseFloat(formData.price) < 0) {
      newErrors.price = 'Le prix doit être un nombre positif ou zéro';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Récupérer les informations supplémentaires
      const selectedVehicle = vehicles.find(v => v.id === formData.vehicleId);
      const selectedDriver = drivers.find(d => d.id === formData.driverId);
      const selectedRoute = routes.find(r => r.id === formData.routeId);
      
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const tripDoc = doc(firestore, 'trips', id);
      await updateDoc(tripDoc, {
        ...formData,
        price: parseFloat(formData.price),
        updatedAt: new Date(),
        routeName: selectedRoute.name,
        vehicleName: selectedVehicle.name,
        driverName: selectedDriver.name
      });
      
      setSnackbar({
        open: true,
        message: 'Voyage mis à jour avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la page de détails après un court délai
      setTimeout(() => {
        navigate(`/admin/trips/${id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error updating trip:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour du voyage',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setOpenConfirmDialog(true);
    } else {
      navigate(`/admin/trips/${id}`);
    }
  };

  const handleConfirmCancel = () => {
    setOpenConfirmDialog(false);
    navigate(`/admin/trips/${id}`);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleCancel}
        style={{ marginBottom: 16 }}
      >
        Retour aux détails
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Modifier le voyage
      </Typography>
      
      <Paper className={classes.paper}>
        {loadingData ? (
          <div className={classes.loading}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginLeft: 16 }}>
              Chargement des données...
            </Typography>
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations de l'itinéraire
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.routeId}>
                  <InputLabel id="route-label">Itinéraire</InputLabel>
                  <Select
                    labelId="route-label"
                    id="routeId"
                    name="routeId"
                    value={formData.routeId}
                    onChange={handleChange}
                    label="Itinéraire"
                    required
                  >
                    {routes.map(route => (
                      <MenuItem key={route.id} value={route.id}>
                        {route.name} ({route.origin} - {route.destination})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.routeId && <FormHelperText>{errors.routeId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                  <KeyboardDateTimePicker
                    className={classes.datePicker}
                    variant="inline"
                    inputVariant="outlined"
                    label="Date et heure de départ"
                    format="dd/MM/yyyy HH:mm"
                    ampm={false}
                    value={formData.departureDate}
                    onChange={handleDateChange('departureDate')}
                    error={!!errors.departureDate}
                    helperText={errors.departureDate}
                    required
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                  <KeyboardDateTimePicker
                    className={classes.datePicker}
                    variant="inline"
                    inputVariant="outlined"
                    label="Date et heure d'arrivée"
                    format="dd/MM/yyyy HH:mm"
                    ampm={false}
                    value={formData.arrivalDate}
                    onChange={handleDateChange('arrivalDate')}
                    error={!!errors.arrivalDate}
                    helperText={errors.arrivalDate}
                    required
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations du véhicule et du chauffeur
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.vehicleId}>
                  <InputLabel id="vehicle-label">Véhicule</InputLabel>
                  <Select
                    labelId="vehicle-label"
                    id="vehicleId"
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleChange}
                    label="Véhicule"
                    required
                  >
                    {vehicles.map(vehicle => (
                      <MenuItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} ({vehicle.type}, {vehicle.capacity} places)
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.vehicleId && <FormHelperText>{errors.vehicleId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.driverId}>
                  <InputLabel id="driver-label">Chauffeur</InputLabel>
                  <Select
                    labelId="driver-label"
                    id="driverId"
                    name="driverId"
                    value={formData.driverId}
                    onChange={handleChange}
                    label="Chauffeur"
                    required
                  >
                    {drivers.map(driver => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.driverId && <FormHelperText>{errors.driverId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informations tarifaires
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="price"
                  label="Prix par passager"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  error={!!errors.price}
                  helperText={errors.price}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                    required
                  >
                    <MenuItem value="Scheduled">Programmé</MenuItem>
                    <MenuItem value="InProgress">En cours</MenuItem>
                    <MenuItem value="Completed">Terminé</MenuItem>
                    <MenuItem value="Cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Informations supplémentaires sur le voyage..."
                />
              </Grid>
            </Grid>
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={loading || !hasChanges}
              >
                {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      {/* Dialog de confirmation pour annuler les modifications */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Abandonner les modifications ?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter cette page ? Toutes les modifications seront perdues.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Continuer l'édition
          </Button>
          <Button onClick={handleConfirmCancel} color="secondary" autoFocus>
            Abandonner les modifications
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TripEditPage;
